<template>
    <div class="mt-6 content">
        <div class="is-flex is-justify-content-space-between is-align-items-flex-start">
            <div class="columns is-gapless is-mobile mr-6" style="max-width: 80%;">
                <div class="column is-narrow"><i class="fad fa-fw fa-memo-pad has-text-white mr-3 is-size-4"></i></div>
                <div class="column">
                    <h3 class="has-text-white">Online Course Notes</h3>
                    <p class="has-text-white">Keep personal notes alongside your coursework &mdash; ideas, resources, and drafts for your reflections. Notes save automatically and stay with this course enrollment.</p>
                </div>
            </div>
            <b-button type="is-success" icon-left="plus" class="ml-4" :class="{'is-invisible': loading}" @click="openNew">
                New Note
            </b-button>
        </div>

        <div class="mt-6 pb-6" v-if="loading">
            <ui-card class="pt-0">
                <div class="py-2 has-text-centered">
                    <i class="fas fa-spin fa-spinner fa-fw mr-2"></i>Loading notes...
                </div>
            </ui-card>
        </div>

        <div class="mt-6 pb-6" v-else>
            <ui-card class="pt-0">

                <div v-if="!notes.length" class="columns is-gapless pt-2">
                    <div class="column is-narrow"><i class="far fa-fw fa-memo-pad mr-2"></i></div>
                    <div class="column">
                        <div class="has-text-weight-bold">You haven't added any notes to this course yet.</div>
                        <div class="mt-1">Click <em>New Note</em> to create your first note.</div>
                    </div>
                </div>

                <div v-else>
                    <div class="note-row" v-for="note in notes" :key="note.id" @click="openNote(note)">
                        <div class="columns is-mobile is-vcentered is-gapless">
                            <div class="column is-narrow" style="align-self: flex-start;">
                                <i class="far fa-fw fa-memo-pad mr-3 is-size-5 has-text-grey"></i>
                            </div>
                            <div class="column">
                                <div class="has-text-weight-bold">{{ note.title }}</div>
                                <div class="is-size-7 has-text-grey">{{ formatDate(note.updated_at || note.created_at) }}</div>
                            </div>
                            <div class="column is-narrow">
                                <span class="row-action is-clickable mr-2" title="Edit" @click.stop="openNote(note)">
                                    <i class="fas fa-pen"></i>
                                </span>
                                <span class="row-action delete-x is-clickable" title="Delete" @click.stop="confirmDelete(note)">
                                    <i class="fas fa-times"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

            </ui-card>
        </div>

        <!-- Note editor drawer (shared with the lesson viewer) -->
        <note-editor-panel
            :is-open="editorOpen"
            :enrollment-id="enrollmentId"
            :note="editingNote"
            @close="editorOpen = false"
            @saved="onNoteSaved"
            @deleted="onNoteDeleted"
        />
    </div>
</template>

<script>
    import {client as http} from "../../http_client";
    import moment from "moment";
    import UiCard from "../../components/UiCard";
    import NoteEditorPanel from "../../components/course/NoteEditorPanel";

    export default {
        components: {UiCard, NoteEditorPanel},
        props: ['enrollmentId'],
        data: function() {
            return {
                loading: true,
                notes: [],
                editorOpen: false,
                editingNote: null
            };
        },
        methods: {
            load: function() {
                this.loading = true;
                http.get(`app/enrollments/${this.enrollmentId}/notes`, {force: true})
                    .then(response => {
                        this.notes = response.data.notes || [];
                    })
                    .catch(() => {
                        this.$buefy.toast.open({message: 'Could not load your notes. Please try again.', type: 'is-danger'});
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },
            openNew: function() {
                this.editingNote = null;
                this.editorOpen = true;
            },
            openNote: function(note) {
                this.editingNote = note;
                this.editorOpen = true;
            },
            onNoteSaved: function(saved) {
                const index = this.notes.findIndex(n => n.id === saved.id);
                if (index >= 0) this.notes.splice(index, 1, saved);
                else this.notes.unshift(saved);
            },
            onNoteDeleted: function(noteId) {
                this.notes = this.notes.filter(n => n.id !== noteId);
            },
            confirmDelete: function(note) {
                this.$buefy.dialog.confirm({
                    title: 'Delete Note',
                    message: `Are you sure you want to delete <b>${this.escapeHtml(note.title)}</b>? This cannot be undone.`,
                    confirmText: 'Delete',
                    type: 'is-danger',
                    onConfirm: () => this.deleteNote(note)
                });
            },
            deleteNote: function(note) {
                http.delete(`app/enrollments/${this.enrollmentId}/notes/${note.id}`, {force: true})
                    .then(() => {
                        this.onNoteDeleted(note.id);
                        if (this.editorOpen && this.editingNote && this.editingNote.id === note.id) {
                            this.editorOpen = false;
                        }
                        this.$buefy.toast.open({message: 'Note deleted.', type: 'is-success'});
                    })
                    .catch(() => {
                        this.$buefy.toast.open({message: 'Could not delete the note. Please try again.', type: 'is-danger'});
                    });
            },
            escapeHtml: function(text) {
                const div = document.createElement('div');
                div.textContent = text;
                return div.innerHTML;
            },
            formatDate: function(date) {
                return moment(date).format('MMM D, YYYY h:mm a');
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style scoped>
    /* Dividers between rows only — no border above the first or below the last */
    .note-row {
        padding: 0.75rem 0.5rem;
        border-bottom: 1px solid #e7e7e7;
        cursor: pointer;
    }
    .note-row:last-child {
        border-bottom: none;
    }
    .note-row:hover {
        background-color: #f7f7f7;
    }

    /* Lightweight row actions — shared look with the Files and Study Log lists */
    .row-action {
        display: inline-block;
        font-size: 1.25rem;
        line-height: 1;
        color: #7a7a7a;
        padding: 0.3rem 0.45rem;
        border: 1px solid #dbdbdb;
        border-radius: 4px;
        transition: color 0.2s, border-color 0.2s;
    }
    .row-action:hover {
        color: #363636;
        border-color: #b5b5b5;
    }
    .row-action.delete-x {
        color: #f14668;
    }
    .row-action.delete-x:hover {
        color: #cc0f35;
    }
</style>
